import type { AttendanceData } from "@/artifacts/teacher/pages/Dashboard";
import { HStack } from "@/components/layout/HStack";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/Collapsible/Collapsible";
import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { Eye, EyeClosed, UsersRound } from "lucide-react";
import { useMemo, useState } from "react";
import { AttendanceTable } from "./AttendanceDataTable";
import { AbsentStudentsTable } from "./AbsentStudentsDataTable";
import { formatNumber } from "@/utils/formatNumber";
import { useSearchParams } from "react-router-dom";

const Attendance = ({
  data,
  loading,
}: {
  data: AttendanceData | null;
  loading: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState<boolean>(true);

  const absentSelection = useMemo(() => {
    const rotationId = searchParams.get("aRot") ?? "";
    const sessionId = searchParams.get("aSesh") ?? "";
    if (!rotationId || !sessionId) return null;
    return { rotationId, sessionId };
  }, [searchParams]);

  const setAbsentSelection = (
    next: { rotationId: string; sessionId: string } | null,
  ) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);

      if (!next) {
        sp.delete("aRot");
        sp.delete("aSesh");
        return sp;
      }

      sp.set("aRot", next.rotationId);
      sp.set("aSesh", next.sessionId);
      return sp;
    });
  };

  const absentStudents = (() => {
    if (!absentSelection || !data) return null;

    const rotation = data.rotations.find(
      (r) => r._id === absentSelection.rotationId,
    );
    if (!rotation) return null;

    const session = rotation.sessions.find(
      (s) => s._id === absentSelection.sessionId,
    );
    if (!session) return null;

    return {
      rotationId: rotation._id,
      rotationDate: rotation.date,
      sessionId: session._id,
      sessionName: session.name,
      students: session.absentStudents,
    };
  })();

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      style={{ marginTop: "-8px" }}
    >
      <VStack gap="2rem">
        <VStack gap={0}>
          <HStack>
            <h3
              style={{
                fontSize: "var(--text-lg)",
                lineHeight: "var(--line-height-lg)",
                fontWeight: "var(--font-bold)",
              }}
            >
              Attendance
            </h3>
            <Tooltip>
              <TooltipTrigger asChild>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon-small">
                    {open ? <EyeClosed /> : <Eye />}
                  </Button>
                </CollapsibleTrigger>
              </TooltipTrigger>
              <TooltipContent>
                {open ? "Hide" : "Show"} attendance section.
              </TooltipContent>
            </Tooltip>
          </HStack>
          <p style={{ color: "var(--muted-foreground)" }}>
            Attendance stats for your explorations
          </p>
        </VStack>
        <CollapsibleContent>
          {loading ? (
            <VStack gap="1rem">
              <HStack gap="1rem">
                <Skeleton style={{ height: "94px", width: "100%" }} />
                <Skeleton style={{ height: "94px", width: "100%" }} />
                <Skeleton style={{ height: "94px", width: "100%" }} />
                <Skeleton style={{ height: "94px", width: "100%" }} />
              </HStack>
              <Skeleton style={{ height: "240px", width: "100%" }} />
            </VStack>
          ) : !data || data.rotations.length < 1 ? (
            <Empty style={{ borderColor: "var(--secondary-border)" }}>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <UsersRound />
                </EmptyMedia>
                <EmptyTitle>No Attendance Records Found</EmptyTitle>
                <EmptyDescription>
                  There were no attendance records found. If you believe this is
                  an error, please refresh.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <VStack gap="1rem">
              <HStack gap="1rem" style={{ flexWrap: "wrap" }}>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Total Records</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {formatNumber(data.totals.records)}
                  </p>
                </DashboardCard>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Present</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {formatNumber(data.totals.present)}
                  </p>
                </DashboardCard>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Absent</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {formatNumber(data.totals.records - data.totals.present)}
                  </p>
                </DashboardCard>
                <DashboardCard
                  style={{
                    flex: "1 1 0%",
                    flexWrap: "nowrap",
                    textWrap: "nowrap",
                  }}
                >
                  <p>Rate</p>
                  <p
                    style={{
                      fontSize: "var(--text-2xl)",
                      lineHeight: "var(--line-height-2xl)",
                      fontWeight: "var(--font-bold)",
                    }}
                  >
                    {(
                      (data.totals.present / data.totals.records) *
                      100
                    ).toFixed(1)}
                    %
                  </p>
                </DashboardCard>
              </HStack>
              <AttendanceTable
                data={data}
                absentStudents={absentStudents}
                setAbsentSelection={setAbsentSelection}
              />
              {absentStudents && (
                <AbsentStudentsTable
                  students={absentStudents.students}
                  rotationDate={absentStudents.rotationDate}
                  sessionName={absentStudents.sessionName}
                />
              )}
            </VStack>
          )}
        </CollapsibleContent>
      </VStack>
    </Collapsible>
  );
};

export { Attendance };
